import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const ITEM_HINTS = [
  "AWP | Asiimov (Field-Tested)",
  "AK-47 | Redline (Field-Tested)",
  "M4A1-S | Printstream (Minimal Wear)",
  "Desert Eagle | Blaze (Factory New)",
  "USP-S | Kill Confirmed (Minimal Wear)",
  "Karambit | Doppler (Factory New)",
  "Sticker | Crown (Foil)",
  "Sport Gloves | Vice (Field-Tested)",
  "Glock-18 | Fade (Factory New)",
  "Souvenir AWP | Dragon Lore (Field-Tested)",
] as const;

type HintPhase = "typing" | "holding" | "erasing";

interface AnimatedSearchFieldProps {
  value: string;
  onValueChange: (value: string) => void;
}

export function AnimatedSearchField({
  value,
  onValueChange,
}: AnimatedSearchFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [hintIndex, setHintIndex] = useState(0);
  const [hint, setHint] = useState("");
  const [phase, setPhase] = useState<HintPhase>("typing");
  const [iconRun, setIconRun] = useState(0);
  const [reduceMotion] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    if (value || reduceMotion) {
      return;
    }

    const target = ITEM_HINTS[hintIndex];
    const timeout = window.setTimeout(() => {
      if (phase === "typing") {
        if (hint.length < target.length) {
          setHint(target.slice(0, hint.length + 1));
        } else {
          setPhase("holding");
        }
        return;
      }

      if (phase === "holding") {
        setPhase("erasing");
        return;
      }

      if (hint.length > 0) {
        setHint(target.slice(0, hint.length - 1));
        return;
      }

      setHintIndex(
        (current) =>
          (current + 1 + Math.floor(Math.random() * (ITEM_HINTS.length - 1))) %
          ITEM_HINTS.length,
      );
      setPhase("typing");
    }, phase === "holding" ? 1450 : phase === "erasing" ? 24 : 46);

    return () => window.clearTimeout(timeout);
  }, [hint, hintIndex, phase, value, reduceMotion]);

  return (
    <label
      className={`item-search${value ? " has-value" : ""}`}
      onPointerDown={() => setIconRun((current) => current + 1)}
    >
      <span
        className={`animated-search-icon${iconRun > 0 ? " is-animating" : ""}`}
        key={iconRun}
        aria-hidden="true"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m21 21-4.34-4.34" />
          <circle cx="11" cy="11" r="8" />
        </svg>
      </span>
      <span className="item-search-hint" aria-hidden="true">
        {reduceMotion ? ITEM_HINTS[0] : hint}
      </span>
      <input
        ref={inputRef}
        type="search"
        value={value}
        aria-label="Поиск предмета"
        onChange={(event) => onValueChange(event.target.value)}
      />
      {value && (
        <button
          className="item-search-clear"
          type="button"
          title="Очистить поиск"
          aria-label="Очистить поиск"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={() => {
            onValueChange("");
            inputRef.current?.focus();
          }}
        >
          <X aria-hidden="true" />
        </button>
      )}
    </label>
  );
}
